import { z } from 'zod';
import {
  dateSchema,
  idSchema,
  nameSchema,
  objectIdSchema,
  optionalStringSchema,
} from '../../validation';
import {
  identityProofSchema,
  imageWithUploadSchema,
  nationalityPersonSchema,
} from '../../common';

/**
 * RAW card reads for this driver — what the document reader (or the
 * driver typing a card's fields) saw, kept verbatim. Editable, never a
 * hard requirement, and never proof of anything.
 *
 * The three layers of a driver's identity:
 *
 * 1. `extraction.*` — raw reads (this object).
 * 2. `legal.id` / `idIssueNumber` / `employerIdentity` — the CURRENT
 *    values, promoted from a read or typed / edited by a person.
 * 3. `legal.carrierIdentity` + `legal.identityProof` — what was proven
 *    (by Bayan, or by staff for `identityProof.by === 'staff'`).
 *
 * Why the raw layer exists: a freelancer often sends only the licence +
 * الاستمارة (no إقامة), the 70… may be on either card, and an OCR misread
 * must stay visible next to the corrected value. See
 * `docs/adr/011-identity-fields.md`.
 */
export const driverIdentityExtractionSchema = z.object({
  /** Where the extract came from — vision reader vs. the driver typing it. */
  source: z.enum(['agent_vision', 'user_typed']).optional(),
  extractedAt: dateSchema.optional(),
  /** رقم الرخصة — corroborates the identity number when no إقامة was sent. */
  licenseNumber: optionalStringSchema(idSchema),
  licenseExpiry: z.string().trim().min(1).optional(),
  /** نوع الرخصة as printed (نقل ثقيل / عمومي / خصوصي …). */
  licenseClass: z.string().trim().min(1).optional(),
  /** تاريخ إصدار الرخصة as printed (Hijri or Gregorian, not normalised). */
  licenseIssueDate: z.string().trim().min(1).optional(),
  /** تاريخ الميلاد on the إقامة / الهوية, as printed. */
  dateOfBirth: z.string().trim().min(1).optional(),
  /** تاريخ الإصدار of the إقامة / الهوية, as printed. */
  idIssueDate: z.string().trim().min(1).optional(),
  /** تاريخ الانتهاء of the إقامة / الهوية, as printed. */
  idExpiry: z.string().trim().min(1).optional(),
  /**
   * هوية صاحب العمل AS READ off the iqama (raw). The current, editable value
   * lives on `legal.employerIdentity`; this one is never overwritten by an
   * edit, so the admin can show "card says X, on file Y" side by side.
   *
   * Read by: `rankCarrierMoiCandidates` (driver tier, after the current
   * `employerIdentity`). Not for: proving anything — it is what vision saw.
   */
  employerIdentity: optionalStringSchema(idSchema),
  /** اسم صاحب العمل printed on the iqama (raw). Display / the refused-numbers label. */
  employerName: z.string().trim().min(1).optional(),
  /**
   * Every other 70… the cards produced for this driver (raw reads), in the
   * reader's preference order. Read by `rankCarrierMoiCandidates` (the
   * candidates tier, after the account). Not for: display as "the" number.
   */
  moiCandidates: z.array(idSchema).optional(),
  /**
   * Look-alike-digit variants (٦↔٩, ٢↔٣, ٧↔٨) of a 70… vision was unsure
   * of. Never a printed read — Bayan tries them only after every real
   * candidate is refused (the planner's last MOI tier).
   */
  moiVariantCandidates: z.array(idSchema).optional(),
  /**
   * Alternate reads of the driver id when two passes / two cards
   * disagreed. Tried only after the id on file is refused.
   */
  identityCandidates: z.array(idSchema).optional(),
  /**
   * @deprecated Folded into `legal.identityProof.carrierMoi` (+
   * `legal.carrierIdentity`). Still read as a fallback until the contract
   * migration; never written.
   */
  moiUsed: optionalStringSchema(idSchema),
  /** Full raw reader output (audit / training). */
  raw: z.record(z.unknown()).optional(),
  /**
   * @deprecated Use `legal.identityProof` with `by: 'staff'`. Still read as
   * a fallback until the contract migration; never written.
   */
  verifiedBy: z
    .object({
      staffUserId: objectIdSchema,
      displayName: nameSchema.optional(),
      at: dateSchema,
    })
    .optional(),
});
export type DriverIdentityExtraction = z.infer<typeof driverIdentityExtractionSchema>;

/**
 * @deprecated Replaced by the shared {@link identityProofSchema} on
 * `legal.identityProof` (same snapshot as the truck's). Still read as a
 * fallback until the contract migration; never written.
 *
 * The exact id / رقم النسخة pair (and the 70…) ELM accepted on a filed
 * وثيقة. Written by issuance after a successful `createTrip`, never by a
 * form.
 *
 * This is what makes a driver "known to Bayan". Issuance reads it to
 * decide how hard to try: a driver whose `legal.id` + `legal.idIssueNumber`
 * still match this proof gets ONE attempt with those values; a driver
 * with no proof, or whose values changed since, is new to Bayan and gets
 * the parallel fan-out over issue-number variants (see
 * `docs/apps/bayan.md` § The issue matrix). Storing the values rather
 * than a bare timestamp is what lets an edited id invalidate the proof
 * on its own.
 */
export const driverBayanProofSchema = z.object({
  identityNumber: idSchema,
  issueNumber: z.number().int().positive(),
  /** The `carrier.moi` on the same accepted filing, when the driver filed under one. */
  carrierMoi: optionalStringSchema(idSchema),
  /** The Bayan trip that proved the pair. */
  tripId: z.string().trim().min(1).optional(),
  provenAt: dateSchema,
});
export type DriverBayanProof = z.infer<typeof driverBayanProofSchema>;

export const driverLegalSchema = z.object({
  /** the full legal name of the driver written in his/her id.
   *
   * Optional — the only hard requirements on a Driver record are
   * `name` and `phone`. This field is recommended for the Logisti
   * integration.
   *
   * Uses `optionalStringSchema` so empty strings from the form get
   * coerced to `undefined` instead of failing `nameSchema.min(1)`.
   */
  fullName: optionalStringSchema(nameSchema),
  /**
   * رقم الهوية / الإقامة — the driver's current national / iqama id
   * (10 digits, 1… Saudi, 2… resident). Written by: promotion from a card
   * read, the driver / dashboard form, staff in the admin, or issuance when
   * an alternate read is the one ELM accepted. Read by: the Bayan payload
   * (`driver.identityNumber`) and `isDriverIdentityProven`.
   * Not for: a 70… company number — that is `employerIdentity`.
   */
  id: optionalStringSchema(idSchema),
  /**
   * Issue number of the driver's identity card ("رقم إصدار الهوية") — the
   * small counter printed on a Saudi national id / iqama that increments each
   * time the card is reissued.
   *
   * Bayan requires this alongside `id` on every government waybill
   * (`driver.issueNumber`, § 5.1), and there is no way to derive it from the
   * identity number. Stored as a number because Bayan types it as one.
   *
   * Optional here because most Driver records pre-date the Logisti connector;
   * issuance blocks with `missing_driver_issue_number` until it's filled in.
   */
  idIssueNumber: z.coerce.number().int().positive().optional(),
  /**
   * هوية صاحب العمل — the CURRENT 70… of the company that employs this
   * driver (printed on a resident's iqama; Saudis have none).
   *
   * Written by: promotion from `extraction.employerIdentity`, the driver
   * typing it, or staff in the admin (a typed 70… on a driver lands here).
   * Read by: `rankCarrierMoiCandidates` (driver tier) and the Bayan mapper
   * for an owner-operator's `carrier.moi`.
   * Not for: "the number Bayan accepted" — that is `carrierIdentity`; and
   * not the account's own CR — that is `Org.legal.registrationId`.
   */
  employerIdentity: optionalStringSchema(idSchema),
  /**
   * @deprecated Overloaded (employer, the account's CR, and what issuance
   * wrote on success). Use `employerIdentity` for the employer and
   * `carrierIdentity` for the accepted number. Still read as a fallback
   * until the contract migration; no longer written by issuance or staff.
   */
  registrationId: optionalStringSchema(idSchema),
  /**
   * الرقم الموحد Bayan ACCEPTED as `carrier.moi` for this driver — the
   * same value as `Vehicle.registration.carrierIdentity` on the accepted
   * filing.
   *
   * Written by: issuance only, on a successful `createTrip`
   * (`recordIdentityProof`). Read by: `rankCarrierMoiCandidates` (proven
   * tier, first) and the "Accepted by Bayan" badge.
   * Not for: anything staff or a user types — those go to
   * `employerIdentity`. Staff never write this field.
   */
  carrierIdentity: optionalStringSchema(idSchema),
  /**
   * The last proven filing for this driver — the SAME snapshot the truck
   * carries (`Vehicle.registration.identityProof`). Compare with
   * `isDriverIdentityProven` / `isPairProven` from `@hero/core`; never
   * trust its presence alone. See {@link identityProofSchema}.
   */
  identityProof: identityProofSchema.optional(),
  idImage: imageWithUploadSchema.optional(),
  driverLicenseImage: imageWithUploadSchema.optional(),
  /** the country the driver is from. Optional — recommended for Logisti. */
  nationality: nationalityPersonSchema.optional(),
  /**
   * Audit snapshot of what the document reader extracted + which MOI Bayan
   * accepted. Additive and always optional — see
   * {@link driverIdentityExtractionSchema}.
   */
  extraction: driverIdentityExtractionSchema.optional(),
  /** @deprecated Use `identityProof`. Read as a fallback only — see {@link driverBayanProofSchema}. */
  bayanProof: driverBayanProofSchema.optional(),
});
export type DriverLegal = z.infer<typeof driverLegalSchema>;
